'use client';

/**
 * @fileoverview `SearchSheetMount` (FRE-816) — root-level mount for the
 * global `SearchSheet` overlay.
 *
 * The underlying `<SearchSheet />` organism was built by WEB-220 (PR #3340)
 * and the AppHeader search button is wired to `useSearchSheet().setOpen(true)`,
 * but the component itself was never instantiated anywhere in the tree:
 * tapping the search button was a silent no-op on every page.
 *
 * This mount lives inside `app/providers.tsx` — the highest shared client
 * boundary above BOTH the consumer surface (AppShellLayout → AppHeader) and
 * the marketing/apex surfaces (DefaultLayout / ApexMarketingChrome →
 * SiteHeader). One mount serves the whole app.
 *
 * Because `useSearchSheet` is backed by a module-scoped `useSyncExternalStore`,
 * there is no provider to add — any descendant that calls `useSearchSheet()`
 * sees the same open/close state without prop drilling.
 *
 * City context: most pages live under a per-city surface, so we read the
 * city via `useCityContext()`. On the apex marketing surface there is no
 * city in context — we fall back to `defaultCityCode()` (Charlotte for the
 * MVP) for query scoping, and pass `displayName` from the registry for
 * human-facing copy (WEB-620 / PL-09). When neither context nor registry
 * yields a display name, SearchSheet renders city-free "Search…" rather
 * than "Search undefined…".
 */

import * as React from 'react';
import { CITY_EDITIONS } from '@loci/city-editions';

import { SearchSheet } from '@/components/organisms/SearchSheet';
import { useSearchSheet } from '@/hooks/useSearchSheet';
import { useCityContext } from '@/lib/city-context.client';
import { defaultCityCode } from '@/lib/cross-host-urls';

/**
 * Root mount for the global `SearchSheet`. Renders the sheet exactly once
 * at the top of the client tree. Open/close state flows through the
 * module-scoped `useSearchSheet` store; the AppHeader search button calls
 * `setOpen(true)` and this component receives the update via the hook.
 */
declare global {
  interface Window {
    /** Playwright-only bridge — set when `NEXT_PUBLIC_E2E_HOOKS=true`. */
    __LOCI_E2E_OPEN_SEARCH_SHEET__?: () => void;
  }
}

export function SearchSheetMount(): React.ReactElement {
  const { open, setOpen } = useSearchSheet();
  const city = useCityContext();
  const cityCode = city?.code ?? defaultCityCode();
  // Prefer live context; fall back to the registry for the defaulted code so
  // apex still says "Search Charlotte…" (same city the sheet scopes to).
  // Never invent a name from the code string itself.
  const cityName =
    city?.displayName ?? CITY_EDITIONS[cityCode as keyof typeof CITY_EDITIONS]?.displayName ?? null;

  // WEB-611 e2e: open the real SearchSheet without AppHeader/auth. Gated on
  // `NEXT_PUBLIC_E2E_HOOKS` so production never exposes the bridge. Inlined at
  // build: a build without the variable never installs it (WEB-1116).
  React.useEffect(() => {
    if (process.env.NEXT_PUBLIC_E2E_HOOKS !== 'true') return;
    window.__LOCI_E2E_OPEN_SEARCH_SHEET__ = () => {
      setOpen(true, { trigger: 'kbd' });
    };
    return () => {
      delete window.__LOCI_E2E_OPEN_SEARCH_SHEET__;
    };
  }, [setOpen]);

  return <SearchSheet open={open} onOpenChange={setOpen} cityCode={cityCode} cityName={cityName} />;
}
